import type { StartSpanOptions as SentryStartSpanOptions } from '@sentry/core';
import type { IProfilerOptions } from '../types/profiler.js';
import { BaseProfiler } from '../base/Profiler.js';
import { SentrySpan } from './Span.js';
import { SentryMark } from './Mark.js';

/**
 * SentryProfiler
 */
export class SentryProfiler extends BaseProfiler<unknown, SentryMark> {
  /**
   * startActiveSpan
   *
   * @param {IProfilerOptions} options
   * @param {callback: (span: unknown) => unknown} callback
   * @returns {void}
   */
  public startActiveSpan(options: IProfilerOptions, callback: (span: unknown) => unknown): void {
    if (this.enabled) {
      this.adapter.startSpanManual(options, callback);
    } else {
      callback(new SentrySpan());
    }
  }

  /**
   * start
   *
   * @param {SentryStartSpanOptions} options
   * @param {boolean} store
   * @returns {Object}
   */
  public start(options: SentryStartSpanOptions, store = false): SentrySpan {
    if (!this.enabled) {
      return new SentrySpan();
    }

    if (!options.name) {
      this.error(`Invalid name '${options.name}' provided for the mark.`);
    } else if (this.findMark(options.name)) {
      this.error(`There is already a mark with name '${options.name}'. Please provide a different name!`);
    }

    const span = new SentrySpan(this.adapter.startInactiveSpan(options), this.enabled);

    if (store) {
      this.storeMark(new SentryMark(options.name, span));
    }

    return span;
  }

  /**
   * end
   *
   * @param {string} name
   * @returns {void}
   */
  public end(name: string): void {
    if (!this.enabled) {
      return;
    }

    const markIndex = this.findMarkIndex(name);

    if (markIndex > -1) {
      this.marks[markIndex].end();

      this.marks = this.marks.filter((mark) => mark.name !== name);
    } else {
      this.error(`There is no existing mark with name ${name}.`);
    }
  }
}
